// Live diagnostics for a HollowScript editor, checked against the native
// runtime's `check()` (`nativeCapabilities.ts`'s own declarations, the same
// ones a real run checks against - `nativeScriptRunner.ts`). Moved off
// `@hollow/script` once `sstk-hollowscript-lang` commit `96966b9` gave
// `check()`'s diagnostics a precise UTF-16 span
// (`spanStartUtf16`/`spanEndUtf16`, named explicitly for Monaco - see
// `crates/hollowscript-wasm/web/README.md`'s "Diagnostics" section): before
// that, the wasm boundary reported only a line number, which would have
// degraded every squiggle to "the whole line" - see `nativeScriptRunner.ts`'s
// own top doc comment for the full history of this decision.
//
// Returns plain line/column marker data (Monaco's `editor.IMarkerData`
// shape, but expressed with no import from `monaco-editor` at all - see
// `tokenize.ts`'s own doc comment for why that boundary matters here too).
// `startColumn`/`endColumn` are computed directly from a diagnostic's
// `spanStartUtf16`/`spanEndUtf16` via `positions.ts`'s `utf16PositionAt`, not
// Monaco's own `model.getPositionAt` (this module takes a plain `source`
// string, not a model) - a JS string is already UTF-16 code units, so a
// UTF-16 offset indexes it directly, with no surrogate-pair conversion
// needed either way.
//
// Sibling scripts reach the checker as `modules` (the same map a real run
// resolves imports against), so an `import ... from "./util"` in the editor
// is clean exactly when it would be at run time, rather than always HS0601.

import type { NativeModules } from '../native/hollowscript.js';
import { STUDIO_CAPABILITY_NAMES, type StudioCapabilityName } from '../capabilityNames.js';
import { buildNativeCapabilityDeclarations } from '../nativeCapabilities.js';
import { getMonacoTooling, type MonacoTooling } from '../nativeTooling.js';
import { DEFAULT_ENTRY_PATH } from './analysis.js';
import { utf16PositionAt } from './positions.js';

export interface HollowScriptMarker {
  readonly severity: 'error' | 'warning';
  readonly message: string;
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
}

/** Shown when the checker itself fails on a source it could not handle. */
const CHECKER_FAILED_MESSAGE =
  'HollowScript could not check this file because the checker failed on it. This is a bug in HollowScript, not in your script.';

const FAILURE_MARKER: HollowScriptMarker = {
  severity: 'error',
  message: CHECKER_FAILED_MESSAGE,
  startLineNumber: 1,
  startColumn: 1,
  endLineNumber: 1,
  endColumn: 2,
};

/** Type-checks `source` against exactly the capabilities the current
 * project/session grants, so a diagnostic here is the same diagnostic a
 * real run would report. Never throws: `check` (the facade's own, and the
 * native `tooling` build's) is meant never to throw, but the wasm boundary
 * can still answer a checker-internal failure (`HollowScriptTooling.check`'s
 * own doc comment - "check() itself failed unexpectedly"), and one such
 * failure would otherwise blank the whole Problems panel, so it is still
 * reported as one visible error marker on the first line instead of being
 * swallowed. */
export async function computeHollowScriptMarkers(
  source: string,
  granted: ReadonlySet<StudioCapabilityName> | undefined,
  entryPath: string = DEFAULT_ENTRY_PATH,
  options: {
    /** The other scripts `source`'s `import`s resolve against, by canonical
     * path (`scriptModules.ts`'s `buildScriptModules`). Without them every
     * `import` is HS0601, "Cannot find the module". */
    readonly modules?: NativeModules;
    /** The tooling instance to check against. Only tests pass this, to
     * exercise the failure path without depending on a bug in the real
     * checker, or to avoid loading the real wasm in a unit test. */
    readonly tooling?: Pick<MonacoTooling, 'check'>;
  } = {},
): Promise<readonly HollowScriptMarker[]> {
  const t = options.tooling ?? (await getMonacoTooling());
  const declarations = buildNativeCapabilityDeclarations(granted ?? new Set(STUDIO_CAPABILITY_NAMES));
  let diagnostics: ReturnType<MonacoTooling['check']>;
  try {
    diagnostics = t.check(source, entryPath, declarations, options.modules ?? {});
  } catch {
    return [FAILURE_MARKER];
  }
  return diagnostics.map((diagnostic) => {
    if (diagnostic.spanStartUtf16 === null) {
      return { severity: diagnostic.severity, message: diagnostic.message, startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 2 };
    }
    const from = diagnostic.spanStartUtf16;
    // A zero-width span (most often an end-of-file diagnostic) still gets at
    // least one character underlined, matching the previous @hollow/script-
    // backed version's own treatment of the same case.
    const to = Math.max(from + 1, diagnostic.spanEndUtf16 ?? from + 1);
    const start = utf16PositionAt(source, from);
    const end = utf16PositionAt(source, to);
    return {
      severity: diagnostic.severity,
      message: diagnostic.message,
      startLineNumber: start.line,
      startColumn: start.column,
      endLineNumber: end.line,
      endColumn: end.column,
    };
  });
}
